import React, { useCallback, useRef } from 'react';

interface DoubleTapRowProps {
    children: React.ReactNode;
    onSingleTap: () => void;
    onDoubleTap: () => void;
    isDisabled?: boolean;
    delay?: number;
    singleClickOnDesktop?: boolean; // If true, mouse click executes action immediately (no double click needed)
    StyledComponent: React.ComponentType<any>;
    componentProps?: Record<string, unknown>;
}

/** Threshold in pixels — if the finger moves more than this, it's a scroll/swipe, not a tap */
const SWIPE_THRESHOLD = 10;

/**
 * Wrapper component for table rows that handles both single tap and double tap.
 * Works on both desktop (mouse) and mobile (touch) devices.
 *
 * Uses CSS `touch-action: manipulation` to:
 * - Eliminate the 300ms click delay on mobile
 * - Disable double-tap-to-zoom gesture
 * - Allow reliable double tap detection
 *
 * Swipe detection: tracks touch start position and ignores touchend events
 * where the finger moved more than SWIPE_THRESHOLD pixels (scroll gesture).
 *
 * @param onSingleTap - Callback for single tap (row selection)
 * @param onDoubleTap - Callback for double tap (navigation)
 * @param isDisabled - If true, all interactions are disabled
 * @param delay - Time in ms to wait for second tap (default: 250ms)
 * @param singleClickOnDesktop - If true, mouse click executes onDoubleTap immediately (no double click needed)
 * @param StyledComponent - The styled component to render (e.g., TrStyled)
 * @param componentProps - Props to pass to the StyledComponent
 */
export const DoubleTapRow: React.FC<DoubleTapRowProps> = ({
    children,
    onSingleTap,
    onDoubleTap,
    isDisabled = false,
    delay = 250, // Reduced from 300ms since touch-action: manipulation eliminates browser delay
    singleClickOnDesktop = false,
    StyledComponent,
    componentProps = {},
}) => {
    const lastTapTimeRef = useRef<number>(0);
    const singleTapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);

    const handleTouchStart = useCallback((event: React.TouchEvent) => {
        const touch = event.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        }
    }, []);

    const handleInteraction = useCallback(
        (event: React.MouseEvent | React.TouchEvent) => {
            if (isDisabled) return;

            const isTouchEvent = event.type === 'touchend';

            // Prevent double firing on touch devices
            if (isTouchEvent) {
                event.preventDefault();

                // Detect swipe (scroll) vs tap: if finger moved more than threshold, ignore
                const touchEvent = event as React.TouchEvent;
                const touch = touchEvent.changedTouches[0];
                if (touchStartRef.current && touch) {
                    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
                    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
                    if (deltaX > SWIPE_THRESHOLD || deltaY > SWIPE_THRESHOLD) {
                        touchStartRef.current = null;
                        return;
                    }
                }
                touchStartRef.current = null;
            }

            // If singleClickOnDesktop is enabled and this is a mouse click, execute action immediately
            if (!isTouchEvent && singleClickOnDesktop) {
                onDoubleTap();
                return;
            }

            // Original behavior for touch (double tap) and mouse when singleClickOnDesktop is false
            const now = Date.now();
            const timeSinceLastTap = now - lastTapTimeRef.current;

            // Clear any pending single tap timeout
            if (singleTapTimeoutRef.current) {
                clearTimeout(singleTapTimeoutRef.current);
                singleTapTimeoutRef.current = null;
            }

            if (timeSinceLastTap < delay && timeSinceLastTap > 0) {
                // Double tap detected - navigate
                lastTapTimeRef.current = 0;
                onDoubleTap();
            } else {
                // First tap - wait for potential second tap before selecting
                lastTapTimeRef.current = now;
                singleTapTimeoutRef.current = setTimeout(() => {
                    onSingleTap();
                    lastTapTimeRef.current = 0;
                }, delay);
            }
        },
        [isDisabled, delay, onSingleTap, onDoubleTap, singleClickOnDesktop]
    );

    return (
        <StyledComponent
            {...componentProps}
            style={{
                touchAction: 'manipulation', // Eliminates 300ms delay and disables double-tap-to-zoom
                cursor: 'pointer',
                ...(componentProps.style as React.CSSProperties)
            }}
            onClick={handleInteraction}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleInteraction}
        >
            {children}
        </StyledComponent>
    );
};

export default DoubleTapRow;
